import { useMemo, useState } from "react";
import { api } from "../lib/api";
import { useResource, formatDate } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../lib/branding";
import { Modal } from "../components/Modal";
import { InitialsAvatar } from "../components/InitialsAvatar";

interface Faq {
    id: string;
    category: string;
    question: string;
    answer: string;
    /** Who the answer is written for; entries default to 'all'. */
    audience?: "all" | "internal" | "partner";
}

interface AskedQuestion {
    id: string;
    category: string;
    question: string;
    status: "pending" | "answered";
    answer: string | null;
    asked_by_name: string | null;
    answered_by_name: string | null;
    created_at: string;
    is_mine: boolean;
}

/** Category display order, matching the reference design's card row. */
const CATEGORY_ORDER = [
    "Leave & Availability", "Training & Compliance", "Participants",
    "Rostering", "Incidents & Risk", "Technical",
];

const CATEGORY_ICONS: Record<string, JSX.Element> = {
    "Leave & Availability": <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
    "Training & Compliance": <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></>,
    "Participants": <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    "Rostering": <><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="m9 16 2 2 4-4" /></>,
    "Incidents & Risk": <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></>,
    "Technical": <><circle cx="12" cy="12" r="9" /><path d="M12 16v.01" /><path d="M12 13a2.6 2.6 0 0 0 1.5-4.7A2.6 2.6 0 0 0 9.4 9.4" /></>,
};

const CategoryIcon = ({ category }: { category: string }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {CATEGORY_ICONS[category] ?? CATEGORY_ICONS.Technical}
    </svg>
);

/** Renders a contact value as a link when it looks like an email address. */
const ContactValue = ({ value }: { value: string }) =>
    value.includes("@")
        ? <a href={`mailto:${value}`}>{value}</a>
        : <span>{value}</span>;

/**
 * Who to reach when the FAQ is not enough — the organisation's own support
 * desk, set by the admin under Settings → branding. The safety contact is a
 * separate line on purpose: a safeguarding concern must never look like it
 * queues behind password resets. Nothing set, nothing shown.
 */
const ContactCard = () => {
    const branding = useBranding();
    const hasAny = branding.supportPhone || branding.supportEmail
        || branding.supportHours || branding.safetyContact;

    return (
        <div className="card">
            <div className="card-head">
                <h2 className="head-with-icon">
                    <span className="head-icon" aria-hidden="true">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                    </span>
                    Contact
                </h2>
            </div>
            <div className="card-body" style={{ display: "grid", gap: "0.55rem", fontSize: "0.85rem" }}>
                {!hasAny && (
                    <p style={{ color: "var(--muted)" }}>
                        Contact your supervisor for urgent matters. An administrator can add
                        support contact details under Settings.
                    </p>
                )}
                {branding.supportHours && (
                    <p style={{ color: "var(--muted)" }}>We're available {branding.supportHours}.</p>
                )}
                {branding.supportPhone && (
                    <div><strong>P:</strong> <a href={`tel:${branding.supportPhone.replace(/\s+/g, "")}`}>{branding.supportPhone}</a></div>
                )}
                {branding.supportEmail && (
                    <div><strong>E:</strong> <ContactValue value={branding.supportEmail} /></div>
                )}
                {branding.safetyContact && (
                    <p style={{ marginTop: "0.3rem" }}>
                        If you have any safety queries or concerns, please contact{" "}
                        <ContactValue value={branding.safetyContact} />.
                    </p>
                )}
            </div>
        </div>
    );
};

/**
 * The help centre. FAQs are curated answers; the Recent Questions rail is live
 * — answered ones are shared knowledge for everyone, pending ones are visible
 * to their asker and to the managers who can answer them (that scoping is
 * row-level security, not this component).
 */
export const AskQuestion = () => {
    const { user } = useAuth();
    const canAnswer = user?.role === "admin" || user?.role === "manager";

    const faqs = useResource<Faq[]>("/faqs");
    const questions = useResource<AskedQuestion[]>("/questions");

    const [search, setSearch] = useState("");
    const [category, setCategory] = useState<string | null>(null);
    const [openFaq, setOpenFaq] = useState<string | null>(null);
    const [asking, setAsking] = useState(false);
    const [answering, setAnswering] = useState<AskedQuestion | null>(null);

    // Partners read partner-facing answers, internal roles read staff ones —
    // 'all' shows to everyone. Server holds the data; this is presentation.
    const audienceOk = (faq: Faq) =>
        !faq.audience || faq.audience === "all" ||
        (user?.role === "partner" ? faq.audience === "partner" : faq.audience === "internal");
    const allFaqs = (faqs.data ?? []).filter(audienceOk);

    const categories = useMemo(() => {
        const counts = new Map<string, number>();
        for (const faq of allFaqs) counts.set(faq.category, (counts.get(faq.category) ?? 0) + 1);
        return CATEGORY_ORDER.filter((c) => counts.has(c))
            .concat([...counts.keys()].filter((c) => !CATEGORY_ORDER.includes(c)))
            .map((name) => ({ name, count: counts.get(name) ?? 0 }));
    }, [allFaqs]);

    const needle = search.trim().toLowerCase();
    const shownFaqs = allFaqs.filter((faq) =>
        (!category || faq.category === category) &&
        (!needle || faq.question.toLowerCase().includes(needle) || faq.answer.toLowerCase().includes(needle))
    );

    if (faqs.error) return <div className="error-banner">{faqs.error}</div>;

    return (
        <div>
            {asking && (
                <AskDialog
                    categories={categories.map((c) => c.name)}
                    onClose={() => setAsking(false)}
                    onAsked={() => { setAsking(false); questions.reload(); }}
                />
            )}
            {answering && (
                <AnswerDialog
                    question={answering}
                    onClose={() => setAnswering(null)}
                    onAnswered={() => { setAnswering(null); questions.reload(); }}
                />
            )}

            <div className="filterbar">
                <div className="filterbar-row">
                    <div className="search">
                        <span className="search-icon" aria-hidden="true">⌕</span>
                        <input
                            type="search" value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search for answers…"
                            aria-label="Search for answers"
                        />
                    </div>
                    <div className="filterbar-action">
                        <button className="btn" onClick={() => setAsking(true)}>Ask a Question</button>
                    </div>
                </div>
            </div>

            <div className="help-categories">
                {categories.map((c) => (
                    <button
                        key={c.name}
                        className={`help-category ${category === c.name ? "on" : ""}`}
                        onClick={() => setCategory(category === c.name ? null : c.name)}
                        aria-pressed={category === c.name}
                    >
                        <CategoryIcon category={c.name} />
                        <span className="cat-name">{c.name}</span>
                        <span className="cat-count">{c.count} FAQ{c.count === 1 ? "" : "s"}</span>
                    </button>
                ))}
            </div>

            <div className="help-grid">
                <div className="card">
                    <div className="card-head">
                        <h2 className="head-with-icon">
                            <span className="head-icon" aria-hidden="true">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="9" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" />
                                </svg>
                            </span>
                            Frequently Asked Questions
                        </h2>
                        {category && (
                            <button className="icon-btn" onClick={() => setCategory(null)}>
                                Clear filter: {category}
                            </button>
                        )}
                    </div>
                    <div className="card-body flush">
                        {shownFaqs.length === 0 ? (
                            <div className="empty" style={{ padding: "1.5rem" }}>
                                Nothing matches{needle ? ` “${search.trim()}”` : ""}. Try a different
                                search, or ask it as a question — that is what the button is for.
                            </div>
                        ) : shownFaqs.map((faq) => {
                            const open = openFaq === faq.id;
                            return (
                                <div key={faq.id} className={`faq-item ${open ? "open" : ""}`}>
                                    <button
                                        className="faq-q"
                                        aria-expanded={open}
                                        onClick={() => setOpenFaq(open ? null : faq.id)}
                                    >
                                        <span className="chip info">{faq.category}</span>
                                        <span className="faq-text">{faq.question}</span>
                                        <svg className="faq-chevron" width="16" height="16" viewBox="0 0 24 24"
                                             fill="none" stroke="currentColor" strokeWidth="2"
                                             strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="m6 9 6 6 6-6" />
                                        </svg>
                                    </button>
                                    <div className="faq-a">
                                        <div className="faq-a-inner">{faq.answer}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div style={{ display: "grid", gap: "1rem", alignContent: "start" }}>
                    <div className="card">
                        <div className="card-head">
                            <h2 className="head-with-icon">
                                <span className="head-icon" aria-hidden="true">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" />
                                    </svg>
                                </span>
                                Recent Questions
                            </h2>
                        </div>
                        <div className="card-body" style={{ display: "grid", gap: "0.85rem" }}>
                            {(questions.data ?? []).length === 0 && (
                                <p style={{ color: "var(--muted)", fontSize: "0.85rem" }}>
                                    No questions yet — yours can be the first.
                                </p>
                            )}
                            {(questions.data ?? []).slice(0, 6).map((q) => (
                                <div key={q.id} className="asked-q">
                                    <div className="asked-q-head">
                                        <span className="chip info">{q.category}</span>
                                        <span className={`chip pill ${q.status === "answered" ? "pass" : "warn"}`}>
                                            {q.status === "answered" ? "Completed" : "Pending"}
                                        </span>
                                    </div>
                                    <div className="asked-q-text">{q.question}</div>
                                    <div className="asked-q-meta">
                                        <InitialsAvatar name={q.asked_by_name} size={24} />
                                        <span>{q.asked_by_name ?? "Unknown"} · {formatDate(q.created_at)}</span>
                                    </div>
                                    {q.answer && (
                                        <div className="asked-q-answer">
                                            <span className="answered-label">
                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                                    <circle cx="12" cy="12" r="9" /><path d="m8.5 12 2.5 2.5 5-5" />
                                                </svg>
                                                Answered{q.answered_by_name ? ` by ${q.answered_by_name}` : ""}
                                            </span>
                                            {q.answer}
                                        </div>
                                    )}
                                    {q.status === "pending" && canAnswer && (
                                        <button className="icon-btn" onClick={() => setAnswering(q)}>Answer</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    <ContactCard />
                </div>
            </div>
        </div>
    );
};

const AskDialog = ({ categories, onClose, onAsked }: {
    categories: string[];
    onClose: () => void;
    onAsked: () => void;
}) => {
    const [category, setCategory] = useState("");
    const [question, setQuestion] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.post("/questions", { category, question: question.trim() });
            onAsked();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not submit");
            setBusy(false);
        }
    };

    return (
        <Modal title="Submit a Question"
            subtitle="Your question is visible to you and the managers who can answer it. Once answered, it joins the shared knowledge base."
            onClose={onClose}
            footer={
                <button className="btn btn-wide" onClick={() => void submit()}
                    disabled={busy || question.trim().length < 5 || !category}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
                    </svg>
                    {busy ? "Submitting…" : "Submit Question"}
                </button>
            }>
            {error && <div className="error-banner">{error}</div>}
            <div className="form-grid">
                <div className="field">
                    <label htmlFor="ask-cat">Category</label>
                    <select id="ask-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                        <option value="" disabled>Select a category</option>
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div className="field">
                    <label htmlFor="ask-q">Your Question</label>
                    <textarea id="ask-q" value={question} maxLength={1000}
                        onChange={(e) => setQuestion(e.target.value)}
                        placeholder="Type your question here…" />
                </div>
            </div>
        </Modal>
    );
};

const AnswerDialog = ({ question, onClose, onAnswered }: {
    question: AskedQuestion;
    onClose: () => void;
    onAnswered: () => void;
}) => {
    const [answer, setAnswer] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setBusy(true);
        setError(null);
        try {
            await api.post(`/questions/${question.id}/answer`, { answer: answer.trim() });
            onAnswered();
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not save");
            setBusy(false);
        }
    };

    return (
        <Modal title="Answer question"
            subtitle="Your answer is published to everyone signed in, with your name on it."
            onClose={onClose}
            footer={<>
                <button className="btn" onClick={() => void submit()} disabled={busy || answer.trim().length < 10}>
                    {busy ? "Publishing…" : "Publish answer"}
                </button>
                <button className="btn ghost" onClick={onClose} disabled={busy}>Cancel</button>
            </>}>
            {error && <div className="error-banner">{error}</div>}
            <p className="confirm-message" style={{ fontWeight: 600 }}>{question.question}</p>
            <p style={{ fontSize: "0.76rem", color: "var(--muted)", marginBottom: "0.7rem" }}>
                Asked by {question.asked_by_name ?? "Unknown"} · {formatDate(question.created_at)}
            </p>
            <div className="field">
                <label htmlFor="ans">Answer</label>
                <textarea id="ans" value={answer} maxLength={4000}
                    onChange={(e) => setAnswer(e.target.value)} style={{ minHeight: 120 }} />
            </div>
        </Modal>
    );
};
